/**
 * Mappers for Add Company V2 Component
 *
 * Converts API response models to state models with pre-calculated values for UI binding
 */

import {
  BillingCycle,
  ChargeType,
  DiscountType,
  ServiceCategory,
} from 'src/app/shared/enums/enums';
import {
  SubscriptionPackageItemResponse,
  SubscriptionPackageResponse,
} from '../../../subscription-plans/models';
import {
  AddonState,
  AppAccessState,
  PricingTier,
  SubscriptionPackageState,
} from './add-company-v2-state.model';

/**
 * Calculate pricing tier for a specific billing cycle
 * Uses API-provided discount type and value for accurate display
 */
function calculatePricingTier(
  pkg: SubscriptionPackageResponse,
  billingCycleValue: number,
  label: string,
  appsMonthlyPrice: number,
): PricingTier {
  let priceBeforeDiscount = 0;
  let priceAfterDiscount = 0;
  let pricePerMonth = 0;
  let priceTotal = 0;
  let discountType = DiscountType.NoDiscount;
  let discountValue = 0; // Raw value from API (e.g., 10 for 10%, or 50 for $50)

  switch (billingCycleValue) {
    case BillingCycle.Monthly:
      priceBeforeDiscount = pkg.pricePerMonthForMonthlyCyclePriorDiscount || appsMonthlyPrice;
      priceAfterDiscount = pkg.pricePerMonthForMonthlyCyclePostDiscount || appsMonthlyPrice;
      discountType = pkg.discountTypePerMonthForMonthlyCycle;
      discountValue = pkg.discountPerMonthForMonthlyCycle || 0;
      pricePerMonth = priceAfterDiscount;
      priceTotal = priceAfterDiscount;
      break;
    case BillingCycle.Yearly_PaidMonthly:
      priceBeforeDiscount = pkg.pricePerMonthForYearlyCyclePriorDiscount || appsMonthlyPrice;
      priceAfterDiscount = pkg.pricePerMonthForYearlyCyclePostDiscount || appsMonthlyPrice;
      discountType = pkg.discountTypePerMonthForYearlyCycle;
      discountValue = pkg.discountPerMonthForYearlyCycle || 0;
      pricePerMonth = priceAfterDiscount;
      priceTotal = priceAfterDiscount * 12;
      break;
    case BillingCycle.Yearly_PaidYearly:
      priceBeforeDiscount = pkg.pricePerYearForYearlyCyclePriorDiscount || appsMonthlyPrice * 12;
      priceAfterDiscount = pkg.pricePerYearForYearlyCyclePostDiscount || appsMonthlyPrice * 12;
      discountType = pkg.discountTypePerYearForYearlyCycle;
      discountValue = pkg.discountPerYearForYearlyCycle || 0;
      pricePerMonth = priceAfterDiscount / 12;
      priceTotal = priceAfterDiscount;
      break;
  }

  // Calculate discount amount and percent from API-provided discount info
  const savedAmount = priceBeforeDiscount - priceAfterDiscount;
  let discountAmount = 0;
  let discountPercent = 0;

  if (discountType === DiscountType.Percentage) {
    discountPercent = discountValue; // e.g., 10 means 10%
    discountAmount = savedAmount; // actual dollar savings
  } else if (discountType === DiscountType.Amount) {
    discountAmount = discountValue; // flat dollar amount
    discountPercent = priceBeforeDiscount > 0 ? (savedAmount / priceBeforeDiscount) * 100 : 0;
  }

  return {
    billingCycle: billingCycleValue,
    label,
    pricePerMonth,
    priceTotal,
    priceBeforeDiscount,
    priceAfterDiscount,
    discountAmount,
    discountPercent,
    discountType,
    hasDiscount: savedAmount > 0
  };
}

/**
 * Transform API response to state model with pre-calculated values
 * Separates apps and addons, calculates prices for ALL billing cycles
 *
 * @param pkg - Subscription package from API
 * @param billingCycle - Selected billing cycle (for legacy priceForThePlan)
 * @returns State model with pre-calculated apps, addons, and pricing tiers
 */
export function transformSubscriptionPackageToState(
  pkg: SubscriptionPackageResponse,
  billingCycle: number = BillingCycle.Monthly,
): SubscriptionPackageState {
  // Multiplier: Yearly Prepaid shows yearly amounts (×12), others show monthly
  const multiplier = billingCycle === BillingCycle.Yearly_PaidYearly ? 12 : 1;

  // Separate apps and addons
  const apps: AppAccessState[] = [];
  const addons: AddonState[] = [];

  pkg.items.forEach((item: SubscriptionPackageItemResponse, index: number) => {
    const isAddon = item.billingService?.category === ServiceCategory.AddOn;

    if (isAddon) {
      console.log('Mapper: Found package addon:', item.billingService?.name, {
        billableServiceId: item.billingServiceId,
        category: item.billingService?.category,
        charges: item.charges
      });
      
      // Filter to only Flat and PerSeat (value > 0) for pricing calculations
      const pricingCharges = item.charges.filter(c => 
        c.type === ChargeType.Flat || (c.type === ChargeType.PerSeat && c.value > 0)
      );
      
      const baseCharge = pricingCharges.find(c => c.type === ChargeType.Flat);
      const perSeatChargeObj = pricingCharges.find(c => c.type === ChargeType.PerSeat);
      
      // Base price only from Flat charge - PerSeat doesn't contribute to base
      const price = baseCharge?.value || 0;
      const includedSeats = baseCharge?.includeSeatsInBasePrice || 0;
      const pricePerSeat = perSeatChargeObj?.value || 0;
      
      if (price === 0 && pricingCharges.length === 0) {
        console.log('Mapper: Addon has no Flat or PerSeat charges, base price = 0');
      }

      const addonState = {
        id: item.id,
        name: item.billingService?.name || '',
        code: item.billingService?.code || '',
        price: price * multiplier,
        priceMonthly: price,
        basePrice: price * multiplier,
        billingServiceId: item.billingServiceId,
        category: item.billingService?.category || ServiceCategory.AddOn,
        displayOrder: index,
        includedSeats: includedSeats,
        currentSeats: includedSeats,
        perSeatPrice: pricePerSeat * multiplier,
        perSeatPriceMonthly: pricePerSeat,
        charges: item.charges,
        isFromPackage: true // Package-included addon
      };
      
      addons.push(addonState);
      console.log('Mapper: Added package addon to array:', addonState);
    } else {
      // App access item - filter to Flat and PerSeat (value > 0) for pricing
      const pricingCharges = item.charges.filter(c => 
        c.type === ChargeType.Flat || (c.type === ChargeType.PerSeat && c.value > 0)
      );
      
      const baseCharge = pricingCharges.find(c => c.type === ChargeType.Flat);
      const pricePerSeat = pricingCharges.find(c => c.type === ChargeType.PerSeat)?.value || 0;
      const includedSeats = baseCharge ? baseCharge.includeSeatsInBasePrice : 0;
      const priceMonthly = baseCharge ? baseCharge.value : 0;

      apps.push({
        id: item.id,
        appId: item.billingService?.appId || 0,
        appName: item.billingService?.name || '',
        includedSeats: includedSeats,
        currentSeats: includedSeats,
        basePrice: priceMonthly * multiplier,
        basePriceMonthly: priceMonthly,
        perSeatPrice: pricePerSeat * multiplier,
        perSeatPriceMonthly: pricePerSeat,
        billingServiceId: item.billingServiceId,
        charges: item.charges, // Keep all charges for display
        displayOrder: index
      });
    }
  });

  // Calculate base monthly price from apps (for fallback)
  const appsMonthlyPrice = apps.reduce((sum, app) => sum + app.basePriceMonthly, 0);

  // Calculate pricing tiers for ALL billing cycles
  const pricingTiers = {
    monthly: calculatePricingTier(pkg, BillingCycle.Monthly, 'Monthly', appsMonthlyPrice),
    yearlyPaidMonthly: calculatePricingTier(
      pkg,
      BillingCycle.Yearly_PaidMonthly,
      'Yearly, Paid Monthly',
      appsMonthlyPrice,
    ),
    yearlyPaidYearly: calculatePricingTier(
      pkg,
      BillingCycle.Yearly_PaidYearly,
      'Yearly, Prepaid',
      appsMonthlyPrice,
    ),
  };

  // Legacy properties for backward compatibility
  let priceForThePlan: number;
  if (billingCycle === BillingCycle.Yearly_PaidYearly) {
    priceForThePlan = pricingTiers.yearlyPaidYearly.priceTotal;
  } else if (billingCycle === BillingCycle.Yearly_PaidMonthly) {
    priceForThePlan = pricingTiers.yearlyPaidMonthly.pricePerMonth;
  } else {
    priceForThePlan = pricingTiers.monthly.pricePerMonth;
  }

  const monthlyPrice = pricingTiers.monthly.pricePerMonth;

  return {
    ...pkg,
    priceForThePlan,
    monthlyPrice,
    pricingTiers,
    apps,
    addons,
    isAssigned: false,
  };
}
